import axios, { isCancel, type AxiosInstance, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios'
import { tryUseNuxtApp, type NuxtApp } from 'nuxt/app'
import md5 from 'md5'
import { v4 as uuidv4 } from 'uuid'
import { useEnvApi } from '../composables/useEnvApi'
import { clearAppCookies, resolveAppCookieDomain } from '../composables/useAppCookie'
import { normalizeCountryCodeValue, useAppStore } from '../stores/app'

export const SKIP_AUTH_HEADER = 'X-Wiki-Skip-Auth'

// 需要返回完整结果的接口路径
const needReturnFullResult = [
  '/market/platform/add',
  '/market/payment/add',
  '/market/licenseCommission/add',
  '/market/licenseTransfer/add',
  '/market/exhibition/add',
  '/market/ib/add',
  '/market/personal/add',
  '/market/team/add',
  '/market/ea/add',
  '/market/rumor/add',
  '/market/quotation/add',
  '/market/technical/add',
]

type AppInternalAxiosRequestConfig = InternalAxiosRequestConfig & {
  __retryInvalidToken?: boolean
}

const SESSION_COOKIE_NAMES = [
  'DJkdikKMG',
  'ViLartidlakd',
  'WsdsfgDGdagYms',
  'userid',
  'logintoken',
  'quitetoken',
  'refreshtime',
  'userbaseinfo',
  'nickavatar',
] as const

const BFP_COOKIE_MAX_AGE = 60 * 60 * 24 * 365 * 10
let cachedClientBfp = ''
const requestNuxtApps = new WeakMap<object, NuxtApp>()

const normalizeHeaderCountryCode = (value: string) => normalizeCountryCodeValue(value) || '156'

const getClientCookie = (name: string): string => {
  if (typeof document === 'undefined') return ''

  const prefix = `${name}=`
  const cookie = document.cookie
    .split(';')
    .map((item) => item.trim())
    .find((item) => item.startsWith(prefix))

  if (!cookie) return ''

  try {
    return decodeURIComponent(cookie.slice(prefix.length))
  } catch {
    return ''
  }
}

const setClientCookie = (name: string, value: string, maxAge: number) => {
  if (typeof document === 'undefined') return

  const domain = resolveAppCookieDomain(window.location.hostname)
  const parts = [`${name}=${encodeURIComponent(value)}`, `max-age=${maxAge}`, 'path=/']
  if (domain) parts.push(`domain=${domain}`)
  document.cookie = parts.join('; ')
}

const resolveRequestBfp = (serverBfp: string): string => {
  if (typeof document === 'undefined') {
    return serverBfp || uuidv4().replace(/-/g, '')
  }

  const cookieBfp = getClientCookie('wiki-bfp')
  if (cookieBfp) {
    cachedClientBfp = cookieBfp
    return cookieBfp
  }

  cachedClientBfp ||= serverBfp || uuidv4().replace(/-/g, '')
  setClientCookie('wiki-bfp', cachedClientBfp, BFP_COOKIE_MAX_AGE)
  return cachedClientBfp
}

const getPathname = (): string => {
  return typeof window !== 'undefined' ? window.location.pathname : ''
}

const getLocalStorageItem = (key: string): string => {
  if (typeof window === 'undefined') return ''
  return window.localStorage.getItem(key) || ''
}

const setLocalStorageItem = (key: string, value: string) => {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(key, value)
}

const removeLocalStorageItem = (key: string) => {
  if (typeof window === 'undefined') return
  window.localStorage.removeItem(key)
}

const clearAccessTokenCache = () => {
  removeLocalStorageItem('v3')
  removeLocalStorageItem('v3expiresIn')
}

// ---------- Token 刷新队列 ----------
let isRefreshing = false
const pendingRequests: Array<(token: string) => void> = []

const subscribeTokenRefresh = (cb: (token: string) => void) => {
  pendingRequests.push(cb)
}

const onTokenRefreshed = (token: string) => {
  pendingRequests.forEach((cb) => cb(token))
  pendingRequests.length = 0
}

const isInvalidAccessTokenError = (err: any = {}) => {
  if (err.response?.status !== 401) return false

  const responseData = err.response?.data || {}
  const errorText = [responseData.error, responseData.error_description, responseData.message, responseData.Message]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

  return errorText.includes('invalid_token') || errorText.includes('expired')
}

// ---------- 错误处理 ----------
const errorHandler = async (err: any = {}) => {
  if (isCancel(err)) {
    return Promise.resolve(err.message)
  }
  // eslint-disable-next-line no-console
  console.error('API Error: ', err?.request?.path, err?.message, err?.status)

  if (isInvalidAccessTokenError(err)) {
    clearAccessTokenCache()

    const originalConfig = err.config
    if (originalConfig && !originalConfig.__retryInvalidToken) {
      originalConfig.__retryInvalidToken = true
      delete originalConfig.headers?.Authorization
      return request(originalConfig)
    }
  }

  if (err.response?.status === 401) {
    clearAccessTokenCache()
    const clearSessionCookies = () => clearAppCookies(SESSION_COOKIE_NAMES)
    const requestConfig = err.config
    const nuxtApp = requestConfig && typeof requestConfig === 'object' ? requestNuxtApps.get(requestConfig) : undefined
    if (nuxtApp) {
      await nuxtApp.runWithContext(clearSessionCookies)
    }
  }

  return Promise.reject(err)
}

// ---------- 创建 axios 实例（仅客户端使用，服务端请使用 Nuxt 内置的 $fetch） ----------
export const request: AxiosInstance = axios.create({
  baseURL: '',
  timeout: process.env.NUXT_APP_ENV === 'production' ? 5000 : 15000,
})

// ---------- 构建 BasicData / basehash ----------
const resolveBasicData = (store: ReturnType<typeof useAppStore>, bfp: string) => {
  if (store.isWiki === '1' || store.cookieHost?.includes('wikifx.hk')) {
    return { base: `999,0,1000,999,0,${bfp},0`, basehash: '999,0,2,999,0,0' }
  }
  if (store.myCountry === '156') {
    return { base: `999,0,1000,999,1,${bfp},1`, basehash: '999,0,1,999,1,1' }
  }
  return { base: `999,0,1000,999,0,${bfp},0`, basehash: '999,0,2,999,0,0' }
}

// ---------- 请求拦截 ----------
request.interceptors.request.use(async (config: AppInternalAxiosRequestConfig) => {
  const nuxtApp = tryUseNuxtApp()
  if (nuxtApp) {
    requestNuxtApps.set(config, nuxtApp)
  }
  const store = useAppStore()
  const shouldSkipAuth = config.headers.get(SKIP_AUTH_HEADER) === '1'
  config.headers.delete(SKIP_AUTH_HEADER)

  // 同步初始化并缓存客户端指纹，确保首屏并发请求使用同一个值。
  const bfp = resolveRequestBfp(store.serverbfp)

  const { base, basehash } = resolveBasicData(store, bfp)

  const currentPath = getPathname()

  // 认证 cookie
  const contentcookie =
    getClientCookie('DJkdikKMG') || getClientCookie('ViLartidlakd') || getClientCookie('WsdsfgDGdagYms') || ''
  const countryCodeCookie = getClientCookie('CountryCode')
  const userIdCookie = getClientCookie('userid')
  const loginTokenCookie = getClientCookie('logintoken')

  // 设置请求头
  Object.assign(config.headers, {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    CountryCode: normalizeHeaderCountryCode(countryCodeCookie || store.CountryCode || '156'),
    'x-forword-for': store.userIp || '',
    LanguageCode: ['zh-hk', 'zh-tw'].includes(store.LanguageCode?.toLowerCase() || '') ? 'zh' : store.LanguageCode,
    'X-User-Id': store.user?.userId || userIdCookie,
    // eslint-disable-next-line typescript-eslint/no-explicit-any
    BasicData: (config.headers as any)?.basicdata1 || base,
    basehash,
    wsc: 2,
    'x-auth-oldtoken': contentcookie,
    'x-auth-token': loginTokenCookie,
    ENVIRONMENT: store.nodeEnv || '',
    startTime: new Date().getTime(),
    metadata: { starttime: new Date().getTime() },
    'x-version': 100,
    Scenecode: `${base}_${md5(currentPath)}_${store.nowtime || ''}`,
  })

  if (typeof window === 'undefined') {
    return config
  }

  if (shouldSkipAuth) {
    return config
  }

  // ---------- 客户端 Token 处理 ----------
  const au = 'v3'
  const token = getLocalStorageItem(au)
  const now = Date.now()
  const localStorageTime = Number(getLocalStorageItem('v3expiresIn')) || 0

  // Token 仍在有效期内，直接使用
  if (token && now < localStorageTime) {
    config.headers.Authorization = `Bearer ${token}`
    return config
  }

  // 需要刷新 Token
  if (!isRefreshing) {
    isRefreshing = true
    try {
      const api = useEnvApi()
      const tokenURL = api.homeClient
      const authorizeData = await axios.post(`${tokenURL}/oauth2/authorize`, {
        response_type: 'code',
        scope: 'email address',
        client_id: CLIENT_ID,
        provision_key: 'wiki-aggregation-layer-key',
        authenticated_userid: 'authenticated_tester',
      })
      const tokenData = await axios.post(`${tokenURL}/oauth2/token`, {
        grant_type: 'authorization_code',
        client_id: CLIENT_ID,
        client_secret: CLIENT_SECRET,
        code: authorizeData.data.redirect_uri.split('=')[1],
      })

      const { access_token: ntoken = '', expires_in: expiresIn = '' } = tokenData.data || {}
      const in1hour = Number(expiresIn) * 1000 + Date.now()

      setLocalStorageItem(au, ntoken)
      setLocalStorageItem('v3expiresIn', String(in1hour))

      config.headers.Authorization = `Bearer ${ntoken}`
      onTokenRefreshed(ntoken)
      return config
    } catch (e) {
      clearAccessTokenCache()
      onTokenRefreshed('')
      throw e
    } finally {
      isRefreshing = false
    }
  }

  // 正在刷新中，排队等待
  return new Promise<InternalAxiosRequestConfig>((resolve) => {
    subscribeTokenRefresh((accessToken) => {
      config.headers.Authorization = `Bearer ${accessToken}`
      resolve(config)
    })
  })
}, errorHandler)

// ---------- 响应拦截 ----------
request.interceptors.response.use((res: AxiosResponse) => {
  const wikifxweb = res.config.url?.includes('wikifxweb') || false
  const { succeed = true, ErrorCode = 200, Data = '', data = '', message = '' } = res.data

  // 判断是否需要返回完整结果
  let needFull = false
  if (wikifxweb && res?.config?.url) {
    needFull = needReturnFullResult.some((item) => res.config.url?.includes(item))
  }

  // eslint-disable-next-line typescript-eslint/no-explicit-any
  let returnObj: any = null
  const requestBaseURL = res.config.baseURL || ''
  const requestURL = res.config.url || ''
  const isV4 = [requestBaseURL, requestURL].some((url) => /https:\/\/api-gw\.tech00[2]\.com/.test(url))

  if (ErrorCode !== 200 || !succeed) {
    returnObj = needFull ? res.data : res.data.data || res.data
  } else {
    returnObj = Data || data || wikifxweb ? Promise.resolve(isV4 ? res.data : Data || data) : res.data
  }

  if (returnObj && needFull) {
    returnObj.message = message
  }

  return returnObj
}, errorHandler)
